import React, { useEffect, useMemo, useState } from 'react';
import { useSignedIn } from '../session';
import { ItemReservationResult, NetworkLocation, SourcingDelivery, SourcingItem } from '../types';
import Dialog from '../components/Dialog';

import { API_BASE as BASE } from '../services/apiBase';

interface Props {
  code: string;
  deliveries: SourcingDelivery[];
  onSaved: () => void;
}

/** „MPO 009" → { kind: 0, code: "009" }; „MAG 021" → { kind: 1, code: "021" }. */
function parseSource(label: string): { sourceKind: number; sourceCode: string } {
  const [prefix, code] = label.split(' ');
  return { sourceKind: prefix === 'MAG' ? 1 : 0, sourceCode: code ?? label };
}

/**
 * Odluka poslovođe odakle se roba diže.
 *
 * Aplikacija ume da pročita gde robe ima, ali ne i odakle će stvarno ići —
 * zaliha je po artiklu, a ne po komadu, i ne zna šta je obećano kome. Poslovođa
 * to zna, jer je prodaja njegova.
 *
 * Odluka je po stavci, pa jedna isporuka sme da se deli: veš mašina iz radnje,
 * frižider iz magacina. Kad postoji mesto koje drži sve, nudi se prvo — jedan
 * dolazak je obično vredniji od pokojeg kilometra.
 */
export default function SourcingPanel({ code, deliveries, onSaved }: Props) {
  const signedIn = useSignedIn();
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Šta je odluka uradila u ERP-u, po otpremnici.
   *
   * Stoji na ekranu jer rezervacija menja tuđe knjige: poslovođa mora da vidi
   * broj naloga, i još više — da vidi kad prenos NIJE prošao, pa da robu odvoji
   * ručno umesto da veruje da je odvojena.
   */
  const [reservations, setReservations] = useState<Record<string, ItemReservationResult[]>>({});

  const keyOf = (documentKey: string, articleCode: string) => `${documentKey}|${articleCode}`;

  /**
   * Stavka za koju poslovođa javlja da je roba naručena.
   *
   * Do sada je operater dobijao golo „nema na stanju nigde" i sa tim nije mogao
   * ništa — tražio je zamenu ili zvao kupca, iako je roba već bila naručena.
   */
  const [promising, setPromising] = useState<{ document: string; item: SourcingItem } | null>(null);
  const [promiseWhere, setPromiseWhere] = useState('');
  const [promiseWhen, setPromiseWhen] = useState('');
  const [promiseNote, setPromiseNote] = useState('');
  const [places, setPlaces] = useState<NetworkLocation[]>([]);

  useEffect(() => {
    void fetch(`${BASE}/api/locations`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then(setPlaces)
      .catch(() => setPlaces([]));
  }, []);

  const promise = async () => {
    if (!promising || promiseWhere === '' || promiseWhen === '') return;

    const [kind, place] = promiseWhere.split(':');

    setBusy(promising.document);
    try {
      const response = await fetch(`${BASE}/api/store/${code}/expected`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentKey: promising.document,
          articleCode: promising.item.articleCode,
          expectedKind: kind,
          expectedCode: place,
          expectedOn: promiseWhen,
          note: promiseNote.trim() || null
        })
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      setPromising(null);
      setPromiseWhere('');
      setPromiseWhen('');
      setPromiseNote('');
      onSaved();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const chosenFor = (delivery: SourcingDelivery, item: SourcingItem): string =>
    choices[keyOf(delivery.documentKey, item.articleCode)]
    ?? item.decidedSource
    ?? '';

  const choose = (documentKey: string, articleCode: string, source: string) =>
    setChoices((current) => ({ ...current, [keyOf(documentKey, articleCode)]: source }));

  /** Jedan klik postavlja sve stavke na isto mesto. Najčešći slučaj. */
  const chooseAll = (delivery: SourcingDelivery, source: string) =>
    setChoices((current) => {
      const next = { ...current };
      for (const item of delivery.items) {
        next[keyOf(delivery.documentKey, item.articleCode)] = source;
      }
      return next;
    });

  const save = async (delivery: SourcingDelivery) => {
    const items = delivery.items
      .map((item) => ({ item, source: chosenFor(delivery, item) }))
      .filter(({ source }) => source !== '')
      .map(({ item, source }) => ({ articleCode: item.articleCode, ...parseSource(source) }));

    if (items.length === 0) {
      setError('Nijedna stavka nema izabrano mesto.');
      return;
    }

    setBusy(delivery.documentKey);
    try {
      const response = await fetch(`${BASE}/api/store/${code}/sourcing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ documentKey: delivery.documentKey, items })
      });

      if (!response.ok) throw new Error(await response.text());

      const result = await response.json();
      setReservations((current) => ({
        ...current,
        [delivery.documentKey]: Array.isArray(result?.reservations) ? result.reservations : []
      }));

      setError(null);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Isporuke na istoj adresi idu zajedno.
   *
   * Kupac koji je kupio šporet i frižider ima dve otpremnice, a to je jedan
   * dolazak na jedna vrata. Dok su stajale kao dve kartice, poslovođa je istu
   * adresu čitao dvaput i lako je promašio da su to isti ljudi.
   *
   * Odluka ostaje po otpremnici — ERP je vodi tako — pa svaka ima svoje polje i
   * svoje dugme; zajedničko je samo zaglavlje.
   */
  const byAddress = useMemo(() => {
    const map = new Map<string, { label: string; customer: string; waitingDays: number; items: SourcingDelivery[] }>();

    for (const delivery of deliveries) {
      const key = `${delivery.address}|${delivery.city}|${delivery.customer}`;
      const bucket = map.get(key);

      if (bucket) {
        bucket.items.push(delivery);
        bucket.waitingDays = Math.max(bucket.waitingDays, delivery.waitingDays);
      } else {
        map.set(key, {
          label: `${delivery.address}, ${delivery.city}`,
          customer: delivery.customer || 'bez imena',
          waitingDays: delivery.waitingDays,
          items: [delivery]
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => b.waitingDays - a.waitingDays);
  }, [deliveries]);

  if (deliveries.length === 0) {
    return <p className="empty">Nema isporuka koje čekaju odluku odakle roba ide.</p>;
  }

  return (
    <>
      <p className="note">
        Aplikacija vidi gde robe ima, ali ne i odakle će stvarno ići. To znaš ti —
        prodaja je tvoja. Odluka se odmah vidi u magacinu, i potpisuje se tvojim
        nalogom ({signedIn.displayName}).
      </p>

      {error && <div className="health health--bad">{error}</div>}

      <ul className="sourcing">
        {byAddress.map((group) => (
          <li key={group.label + group.customer}>
            <p className="sourcing__head">
              {group.label}
              <span className="muted">
                {' '}· {group.customer} · čeka {group.waitingDays} d
                {group.items.length > 1 && ` · ${group.items.length} otpremnice`}
              </span>
            </p>

            {group.items.map((delivery) => {
              const decided = delivery.items.every((i) => chosenFor(delivery, i) !== '');
              const stale = delivery.items.some((i) => !i.stillAvailable);

              return (
                <div key={delivery.documentKey} className="sourcing__doc">
                  {group.items.length > 1 && (
                    <p className="muted sourcing__dockey">Otpremnica {delivery.documentKey}</p>
                  )}

              {stale && (
                <p className="sourcing__stale">
                  Ranija odluka pokazuje na mesto gde robe više nema na stanju.
                  Proveri i potvrdi ponovo.
                </p>
              )}

              {delivery.singleStop.length > 0 && (
                <p className="sourcing__all">
                  Sve odjednom:{' '}
                  {delivery.singleStop.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      className="link"
                      onClick={() => chooseAll(delivery, option.code)}
                    >
                      {option.name}
                    </button>
                  ))}
                </p>
              )}

              <ul className="sourcing__items">
                {delivery.items.map((item) => (
                  <li key={item.articleCode}>
                    <span className="sourcing__what">
                      {item.articleName} <span className="muted">× {item.quantity}</span>
                    </span>

                    {item.sources.length === 0 ? (
                      <span className="sourcing__none">
                        nema na stanju nigde
                        <button
                          type="button"
                          className="link"
                          onClick={() => {
                            setPromising({ document: delivery.documentKey, item });
                            setPromiseWhere('');
                            setPromiseWhen('');
                            setPromiseNote('');
                          }}
                        >
                          naručeno je, stiže
                        </button>
                      </span>
                    ) : (
                      <select
                        value={chosenFor(delivery, item)}
                        onChange={(e) => choose(delivery.documentKey, item.articleCode, e.target.value)}
                      >
                        <option value="">— odakle se diže —</option>
                        {item.sources.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.name}
                            {option.available !== undefined && ` — ${option.available} kom`}
                          </option>
                        ))}
                      </select>
                    )}

                    {/* Mesto koje drži tačno koliko treba je najčešće izložbeni
                        komad. Ne tvrdi se, samo se istakne da se vidi. */}
                    {item.sources.some((o) => o.available === item.quantity)
                      && item.sources.length > 1 && (
                      <span className="sourcing__last">poslednji komad na nekom mestu</span>
                    )}

                    {item.decidedBy && (
                      <span className="muted sourcing__who">odlučio {item.decidedBy}</span>
                    )}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className="primary"
                disabled={busy === delivery.documentKey || !decided}
                onClick={() => void save(delivery)}
              >
                {busy === delivery.documentKey ? 'Čuvam…' : 'Potvrdi odakle ide'}
              </button>

                  {(reservations[delivery.documentKey] ?? []).map((outcome) => (
                    <p
                      key={outcome.articleCode}
                      className={outcome.reserved ? 'sourcing__reserved' : 'sourcing__stale'}
                    >
                      {outcome.articleCode}: {outcome.message}
                    </p>
                  ))}
                </div>
              );
            })}
          </li>
        ))}
      </ul>

      {promising && (
        <Dialog
          title={`Naručeno: ${promising.item.articleName}`}
          description={
            <>Operater će umesto „nema na stanju nigde" videti gde i kad roba
            stiže, pa neće tražiti zamenu ni zvati kupca bez potrebe. Ovim se
            ništa ne rezerviše i ništa ne upisuje u ERP.</>
          }
          confirmLabel="Javi operateru"
          busy={busy === promising.document}
          disabled={promiseWhere === '' || promiseWhen === ''}
          onCancel={() => setPromising(null)}
          onConfirm={() => void promise()}
        >
          <label>
            Gde stiže
            <select value={promiseWhere} onChange={(e) => setPromiseWhere(e.target.value)}>
              <option value="">— izaberi —</option>
              {places.map((place) => (
                <option key={`${place.kind}:${place.code}`} value={`${place.kind}:${place.code}`}>
                  {place.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Kad se očekuje
            <input
              type="date"
              value={promiseWhen}
              onChange={(e) => setPromiseWhen(e.target.value)}
            />
          </label>

          <label>
            Napomena (može ostati prazno)
            <input
              value={promiseNote}
              onChange={(e) => setPromiseNote(e.target.value)}
              placeholder="npr. potvrđeno kod dobavljača, kamion u sredu"
            />
          </label>
        </Dialog>
      )}
    </>
  );
}
